import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { AlertTriangle, Ban, CalendarDays, CheckCircle2, Copy, FileText, Link as LinkIcon, RotateCcw, Upload, X } from "lucide-react";
import * as Effect from "effect/Effect";
import { pipe } from "effect/Function";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { ApiClient } from "@/api/client";
import {
  IgnoreBankRowsResponseSchema,
  ImportBankRowsResponseSchema,
  LinkExistingBankRowResponseSchema,
  ReconcileBankImportResponseSchema,
  type BankImportStatus,
  type ExpenseMatchCandidate,
  type ReconciledBankImportRow,
  type RecurringPriceChange,
} from "@/api/schemas/bank-import.schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingState } from "@/components/ui/loading-state";
import { ResponsiveContainer } from "@/components/layout/responsive-grid";
import { MainLayer } from "@/effects/layers";
import { runEffect } from "@/effects/runtime";
import { QUERY_KEYS } from "@/lib/constants";
import { formatCurrency } from "@/lib/utils";
import { parseBankCsv, type ParsedBankCsvRow } from "@/lib/bank-csv-parser";
import { useGroupCategories } from "@/hooks/queries/useCategories";
import { useActiveGroup } from "@/stores/group.store";

interface ImportReviewRow extends ParsedBankCsvRow {
  bank_import_row_id: string;
  status: BankImportStatus;
  possible_matches: ExpenseMatchCandidate[];
  linked_expense?: ExpenseMatchCandidate | null;
  recurring_price_change?: RecurringPriceChange | null;
  draftDescription: string;
  selectedCategoryId: string;
  selectedMatchId?: string;
  selected: boolean;
  busy: boolean;
}

interface DuplicateGroup {
  id: string;
  rows: ImportReviewRow[];
  amount: number;
  currency_code: string;
}

const statusLabel: Record<BankImportStatus, string> = {
  imported: "Imported",
  ignored: "Ignored",
  possible_match: "Possible match",
  recurring_price_change: "Price change?",
  ready: "Ready",
  needs_category: "Needs category",
};

function statusVariant(status: BankImportStatus): "default" | "secondary" | "outline" | "destructive" {
  if (status === "imported") return "default";
  if (status === "ignored") return "secondary";
  if (status === "possible_match" || status === "recurring_price_change") return "outline";
  if (status === "needs_category") return "destructive";
  return "outline";
}

function effectiveStatus(row: ImportReviewRow): BankImportStatus {
  if (
    row.status === "imported"
    || row.status === "ignored"
    || row.status === "possible_match"
    || row.status === "recurring_price_change"
  ) {
    return row.status;
  }
  return row.selectedCategoryId ? "ready" : "needs_category";
}

function canImport(row: ImportReviewRow) {
  const status = effectiveStatus(row);
  return row.selectedCategoryId
    && (status === "ready" || status === "possible_match" || status === "recurring_price_change");
}

function monthKey(date: string) {
  return date.slice(0, 7);
}

function monthLabel(key: string) {
  return new Date(`${key}-01T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function isWithinDateRange(row: ImportReviewRow, from: string, to: string) {
  if (from && row.transaction_date < from) return false;
  if (to && row.transaction_date > to) return false;
  return true;
}

function dateMs(date: string) {
  return new Date(`${date}T00:00:00.000Z`).getTime();
}

function daysBetween(left: string, right: string) {
  return Math.abs(dateMs(left) - dateMs(right)) / (24 * 60 * 60 * 1000);
}

function duplicateSignature(row: ImportReviewRow) {
  const merchant = (row.merchant_key || row.normalized_label || row.raw_label)
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  const amountCents = Math.round(Number(row.amount) * 100);
  return `${row.currency_code}|${amountCents}|${merchant}`;
}

function findDuplicateGroups(rows: ImportReviewRow[], dayWindow = 3): DuplicateGroup[] {
  const rowsBySignature = new Map<string, ImportReviewRow[]>();
  for (const row of rows) {
    const signature = duplicateSignature(row);
    rowsBySignature.set(signature, [...(rowsBySignature.get(signature) || []), row]);
  }

  const duplicateGroups: DuplicateGroup[] = [];
  for (const signatureRows of rowsBySignature.values()) {
    if (signatureRows.length < 2) continue;

    const sortedRows = [...signatureRows].sort((left, right) =>
      left.transaction_date.localeCompare(right.transaction_date)
    );
    const duplicateIds = new Set<string>();

    for (let leftIndex = 0; leftIndex < sortedRows.length; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < sortedRows.length; rightIndex += 1) {
        if (daysBetween(sortedRows[leftIndex].transaction_date, sortedRows[rightIndex].transaction_date) > dayWindow) {
          break;
        }
        duplicateIds.add(sortedRows[leftIndex].client_row_id);
        duplicateIds.add(sortedRows[rightIndex].client_row_id);
      }
    }

    if (duplicateIds.size > 1) {
      const duplicateRows = sortedRows.filter((row) => duplicateIds.has(row.client_row_id));
      duplicateGroups.push({
        id: duplicateRows.map((row) => row.client_row_id).join(":"),
        rows: duplicateRows,
        amount: duplicateRows[0].amount,
        currency_code: duplicateRows[0].currency_code,
      });
    }
  }

  return duplicateGroups.sort((left, right) =>
    right.rows[0].transaction_date.localeCompare(left.rows[0].transaction_date)
  );
}

function toReviewRows(
  parsedRows: ParsedBankCsvRow[],
  reconciledRows: ReconciledBankImportRow[],
  existingRows: ImportReviewRow[] = []
): ImportReviewRow[] {
  const existingByClientId = new Map(existingRows.map((row) => [row.client_row_id, row]));
  const reconciledByClientId = new Map(reconciledRows.map((row) => [row.client_row_id, row]));

  return parsedRows.map((parsedRow) => {
    const existing = existingByClientId.get(parsedRow.client_row_id);
    const reconciled = reconciledByClientId.get(parsedRow.client_row_id);
    if (!reconciled) {
      throw new Error(`Missing reconciliation for ${parsedRow.client_row_id}`);
    }

    return {
      ...parsedRow,
      bank_import_row_id: reconciled.bank_import_row_id,
      status: reconciled.status,
      possible_matches: reconciled.possible_matches,
      linked_expense: reconciled.linked_expense,
      recurring_price_change: reconciled.recurring_price_change,
      draftDescription: existing?.draftDescription
        || reconciled.suggested_description
        || parsedRow.normalized_label,
      selectedCategoryId: existing?.selectedCategoryId
        || reconciled.suggested_category_id
        || "",
      selectedMatchId: existing?.selectedMatchId || reconciled.possible_matches[0]?.id,
      selected: existing?.selected ?? false,
      busy: false,
    };
  });
}

export const ExpenseImportPage: React.FC = () => {
  const { activeGroupId } = useActiveGroup();
  const queryClient = useQueryClient();
  const { data: categories = [], isLoading: categoriesLoading } = useGroupCategories(activeGroupId || "");
  const [parsedRows, setParsedRows] = React.useState<ParsedBankCsvRow[]>([]);
  const [reviewRows, setReviewRows] = React.useState<ImportReviewRow[]>([]);
  const [creditCount, setCreditCount] = React.useState(0);
  const [skippedCount, setSkippedCount] = React.useState(0);
  const [isParsing, setIsParsing] = React.useState(false);
  const [isReconciling, setIsReconciling] = React.useState(false);
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [duplicatesOnly, setDuplicatesOnly] = React.useState(false);

  const reconcileRows = React.useCallback(async (
    rows: ParsedBankCsvRow[],
    existing: ImportReviewRow[] = []
  ) => {
    if (!activeGroupId || rows.length === 0) return;

    setIsReconciling(true);
    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.post(
            `/groups/${activeGroupId}/bank-import/reconcile`,
            {
              source_type: "credit_agricole_csv_v1",
              rows,
            },
            ReconcileBankImportResponseSchema
          );
        }),
        Effect.provide(MainLayer)
      );
      const result = await runEffect(program);
      setReviewRows(toReviewRows(rows, result.rows, existing));
    } catch (error) {
      toast.error("Failed to reconcile CSV rows");
    } finally {
      setIsReconciling(false);
    }
  }, [activeGroupId]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsParsing(true);
    try {
      const parsed = await parseBankCsv(await file.arrayBuffer());
      setParsedRows(parsed.debitRows);
      setCreditCount(parsed.creditCount);
      setSkippedCount(parsed.skippedCount);
      setDateFrom("");
      setDateTo("");
      setDuplicatesOnly(false);
      await reconcileRows(parsed.debitRows);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to parse CSV");
    } finally {
      setIsParsing(false);
      event.target.value = "";
    }
  };

  const updateRow = (clientRowId: string, updater: (row: ImportReviewRow) => ImportReviewRow) => {
    setReviewRows((rows) => rows.map((row) => row.client_row_id === clientRowId ? updater(row) : row));
  };

  const importRows = async (rowsToImport: ImportReviewRow[]) => {
    if (!activeGroupId || rowsToImport.length === 0) return;

    const importableRows = rowsToImport.filter(canImport);
    if (importableRows.length === 0) {
      toast.error("Select rows with a category before importing");
      return;
    }

    setReviewRows((rows) => rows.map((row) =>
      importableRows.some((candidate) => candidate.client_row_id === row.client_row_id)
        ? { ...row, busy: true }
        : row
    ));

    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.post(
            `/groups/${activeGroupId}/bank-import/import`,
            {
              rows: importableRows.map((row) => ({
                bank_import_row_id: row.bank_import_row_id,
                description: row.draftDescription,
                category_id: row.selectedCategoryId,
                amount: row.amount,
                currency_code: row.currency_code,
                date: row.transaction_date,
              })),
            },
            ImportBankRowsResponseSchema
          );
        }),
        Effect.provide(MainLayer)
      );
      const result = await runEffect(program);
      const importedIds = new Set(result.imported.map((row) => row.bank_import_row_id));

      setReviewRows((rows) => rows.map((row) =>
        importedIds.has(row.bank_import_row_id)
          ? {
              ...row,
              status: "imported",
              selected: false,
              busy: false,
              linked_expense: {
                id: result.imported.find((imported) => imported.bank_import_row_id === row.bank_import_row_id)?.expense_id || "",
                description: row.draftDescription,
                amount: row.amount,
                currency_code: row.currency_code,
                date: row.transaction_date,
              },
            }
          : { ...row, busy: false }
      ));

      if (result.imported.length > 0) {
        toast.success(`Imported ${result.imported.length} transaction${result.imported.length === 1 ? "" : "s"}`);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPENSES] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRANSACTIONS] });
      }
      if (result.failed.length > 0) {
        toast.error(`${result.failed.length} transaction${result.failed.length === 1 ? "" : "s"} failed to import`);
      }
    } catch (error) {
      toast.error("Failed to import selected rows");
      setReviewRows((rows) => rows.map((row) => ({ ...row, busy: false })));
    }
  };

  const setIgnored = async (rowsToUpdate: ImportReviewRow[], ignored: boolean) => {
    if (!activeGroupId || rowsToUpdate.length === 0) return;

    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.post(
            `/groups/${activeGroupId}/bank-import/${ignored ? "ignore" : "unignore"}`,
            { bank_import_row_ids: rowsToUpdate.map((row) => row.bank_import_row_id) },
            IgnoreBankRowsResponseSchema
          );
        }),
        Effect.provide(MainLayer)
      );
      await runEffect(program);

      if (ignored) {
        const ignoredIds = new Set(rowsToUpdate.map((row) => row.bank_import_row_id));
        setReviewRows((rows) => rows.map((row) =>
          ignoredIds.has(row.bank_import_row_id)
            ? { ...row, status: "ignored", selected: false }
            : row
        ));
      } else {
        await reconcileRows(parsedRows, reviewRows);
      }
    } catch (error) {
      toast.error(ignored ? "Failed to ignore rows" : "Failed to undo ignore");
    }
  };

  const linkExisting = async (row: ImportReviewRow) => {
    if (!activeGroupId || !row.selectedMatchId) return;

    updateRow(row.client_row_id, (current) => ({ ...current, busy: true }));
    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.post(
            `/groups/${activeGroupId}/bank-import/link-existing`,
            {
              bank_import_row_id: row.bank_import_row_id,
              expense_id: row.selectedMatchId,
            },
            LinkExistingBankRowResponseSchema
          );
        }),
        Effect.provide(MainLayer)
      );
      await runEffect(program);
      const linked = row.possible_matches.find((match) => match.id === row.selectedMatchId) || null;
      updateRow(row.client_row_id, (current) => ({
        ...current,
        status: "imported",
        linked_expense: linked,
        selected: false,
        busy: false,
      }));
      toast.success("Linked existing expense");
    } catch (error) {
      updateRow(row.client_row_id, (current) => ({ ...current, busy: false }));
      toast.error("Failed to link existing expense");
    }
  };

  /**
   * Resolve a detected subscription price change: retroactively update the
   * rule's amount from the affected occurrence date, then re-reconcile the
   * whole upload — the corrected occurrences now exact-match their bank rows
   * and auto-link (including later months changed by the same price bump).
   */
  const updateSubscriptionAndLink = async (row: ImportReviewRow) => {
    if (!activeGroupId || !row.recurring_price_change) return;
    const change = row.recurring_price_change;

    updateRow(row.client_row_id, (current) => ({ ...current, busy: true }));
    try {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          return yield* apiClient.put(
            `/groups/${activeGroupId}/recurring-expenses/${change.recurring_expense_id}`,
            {
              amount: change.actual_amount,
              effective_date: change.occurrence_date,
            }
          );
        }),
        Effect.provide(MainLayer)
      );
      await runEffect(program);
      toast.success(
        `${change.rule_description}: ${formatCurrency(change.expected_amount, row.currency_code)} → ${formatCurrency(change.actual_amount, row.currency_code)} from ${change.occurrence_date}`
      );
      // Re-reconcile the upload: corrected occurrences now exact-match.
      await reconcileRows(parsedRows, reviewRows);
    } catch (error) {
      updateRow(row.client_row_id, (current) => ({ ...current, busy: false }));
      toast.error("Failed to update the subscription");
    }
  };

  const dateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);
  const dateBounds = React.useMemo(() => {
    if (reviewRows.length === 0) return { from: "", to: "" };
    const dates = reviewRows.map((row) => row.transaction_date).sort();
    return { from: dates[0], to: dates[dates.length - 1] };
  }, [reviewRows]);
  const duplicateGroups = React.useMemo(() => findDuplicateGroups(reviewRows), [reviewRows]);
  const duplicateRowIds = React.useMemo(() => new Set(
    duplicateGroups.flatMap((group) => group.rows.map((row) => row.client_row_id))
  ), [duplicateGroups]);
  const duplicateRowsById = React.useMemo(() => {
    const map = new Map<string, ImportReviewRow[]>();
    for (const group of duplicateGroups) {
      for (const row of group.rows) {
        map.set(
          row.client_row_id,
          group.rows.filter((candidate) => candidate.client_row_id !== row.client_row_id)
        );
      }
    }
    return map;
  }, [duplicateGroups]);
  const visibleRows = React.useMemo(() => (
    dateRangeInvalid
      ? []
      : reviewRows.filter((row) =>
        isWithinDateRange(row, dateFrom, dateTo)
        && (!duplicatesOnly || duplicateRowIds.has(row.client_row_id))
      )
  ), [dateFrom, dateRangeInvalid, dateTo, duplicateRowIds, duplicatesOnly, reviewRows]);
  const groupedRows = React.useMemo(() => {
    const groups = new Map<string, ImportReviewRow[]>();
    for (const row of visibleRows) {
      const key = monthKey(row.transaction_date);
      groups.set(key, [...(groups.get(key) || []), row]);
    }
    return Array.from(groups.entries())
      .sort(([left], [right]) => right.localeCompare(left))
      .map(([key, rows]) => ({
        key,
        label: monthLabel(key),
        rows: [...rows].sort((left, right) =>
          right.transaction_date.localeCompare(left.transaction_date)
        ),
        amount: rows.reduce((sum, row) => sum + Number(row.amount), 0),
      }));
  }, [visibleRows]);
  const readyRows = reviewRows.filter((row) => effectiveStatus(row) === "ready");
  const visibleReadyRows = visibleRows.filter((row) => effectiveStatus(row) === "ready");
  const selectedVisibleReadyRows = visibleRows.filter((row) => row.selected && effectiveStatus(row) === "ready");
  const selectedVisibleRows = visibleRows.filter((row) => row.selected);
  const summary = {
    imported: reviewRows.filter((row) => row.status === "imported").length,
    ignored: reviewRows.filter((row) => row.status === "ignored").length,
    possible: reviewRows.filter((row) => row.status === "possible_match").length,
    duplicates: duplicateRowIds.size,
    needsCategory: reviewRows.filter((row) => effectiveStatus(row) === "needs_category").length,
    ready: readyRows.length,
  };

  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="flex min-h-[400px] items-center justify-center text-center">
          <div>
            <h1 className="text-2xl font-bold">Import CSV</h1>
            <p className="mt-2 text-sm text-muted-foreground">Select a group before importing transactions.</p>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (categoriesLoading) {
    return <LoadingState message="Loading import data..." />;
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Import CSV</h1>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <RouterLink to="/expenses">Transactions</RouterLink>
            </Button>
            <Button asChild>
              <label className="cursor-pointer">
                <Upload className="mr-2 h-4 w-4" />
                Upload CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="sr-only"
                  onChange={handleFileChange}
                  disabled={isParsing || isReconciling}
                />
              </label>
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Debits</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{reviewRows.length}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Credits</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{creditCount}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Ready</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{summary.ready}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Needs Category</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{summary.needsCategory}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Possible</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{summary.possible}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Imported</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{summary.imported}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Duplicates</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{summary.duplicates}</CardContent>
          </Card>
        </div>

        {skippedCount > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            <span>{skippedCount} row{skippedCount === 1 ? "" : "s"} skipped.</span>
          </div>
        )}

        {reviewRows.length > 0 && (
          <div className="rounded-md border bg-background p-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                  <span className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Date range
                  </span>
                  {duplicatesOnly && <Badge variant="outline">Duplicates only</Badge>}
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {visibleRows.length} of {reviewRows.length} debit row{reviewRows.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-[minmax(150px,1fr)_minmax(150px,1fr)_auto_auto] sm:items-end">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="bank-import-from">From</label>
                  <Input
                    id="bank-import-from"
                    type="date"
                    value={dateFrom}
                    min={dateBounds.from}
                    max={dateBounds.to}
                    onChange={(event) => setDateFrom(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="bank-import-to">To</label>
                  <Input
                    id="bank-import-to"
                    type="date"
                    value={dateTo}
                    min={dateBounds.from}
                    max={dateBounds.to}
                    onChange={(event) => setDateTo(event.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                  }}
                  disabled={!dateFrom && !dateTo}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
                <Button
                  type="button"
                  variant={duplicatesOnly ? "default" : "outline"}
                  onClick={() => setDuplicatesOnly((current) => !current)}
                  disabled={duplicateRowIds.size === 0}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicates
                </Button>
              </div>
            </div>
            {dateRangeInvalid && (
              <div className="mt-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <span>Start date must be before end date.</span>
              </div>
            )}
          </div>
        )}

        {duplicateGroups.length > 0 && (
          <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Copy className="h-4 w-4" />
              <span>{duplicateRowIds.size} possible duplicate row{duplicateRowIds.size === 1 ? "" : "s"} across {duplicateGroups.length} group{duplicateGroups.length === 1 ? "" : "s"}.</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-amber-300 bg-white/70 text-amber-950 hover:bg-white"
              onClick={() => setDuplicatesOnly((current) => !current)}
            >
              {duplicatesOnly ? "Show all" : "Review duplicates"}
            </Button>
          </div>
        )}

        {reviewRows.length > 0 && (
          <div className="flex flex-col gap-2 rounded-md border bg-background p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={visibleReadyRows.length > 0 && selectedVisibleReadyRows.length === visibleReadyRows.length}
                onCheckedChange={(checked) => {
                  setReviewRows((rows) => rows.map((row) =>
                    visibleRows.some((visibleRow) => visibleRow.client_row_id === row.client_row_id)
                      && effectiveStatus(row) === "ready"
                      ? { ...row, selected: Boolean(checked) }
                      : row
                  ));
                }}
              />
              <span className="text-sm text-muted-foreground">{selectedVisibleRows.length} selected in view</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => importRows(selectedVisibleReadyRows)}
                disabled={selectedVisibleReadyRows.length === 0}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Import selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIgnored(selectedVisibleRows, true)}
                disabled={selectedVisibleRows.length === 0}
              >
                <Ban className="mr-2 h-4 w-4" />
                Ignore selected
              </Button>
            </div>
          </div>
        )}

        {(isParsing || isReconciling) && <LoadingState message={isParsing ? "Parsing CSV..." : "Reconciling transactions..."} />}

        {reviewRows.length === 0 && !isParsing && !isReconciling && (
          <div className="flex min-h-[280px] items-center justify-center rounded-md border border-dashed text-center">
            <div>
              <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h2 className="font-semibold">No CSV loaded</h2>
            </div>
          </div>
        )}

        {reviewRows.length > 0 && (
          <div className="space-y-3">
            {visibleRows.length === 0 ? (
              <div className="flex min-h-[220px] items-center justify-center rounded-md border border-dashed text-center">
                <div>
                  <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                  <h2 className="font-semibold">No rows in this range</h2>
                </div>
              </div>
            ) : groupedRows.map((group) => (
              <section key={group.key} className="space-y-3">
                <div className="flex flex-col gap-1 border-b pb-2 sm:flex-row sm:items-end sm:justify-between">
                  <h2 className="text-lg font-semibold">{group.label}</h2>
                  <div className="text-sm text-muted-foreground">
                    {group.rows.length} transaction{group.rows.length === 1 ? "" : "s"} · {formatCurrency(group.amount, group.rows[0]?.currency_code || "EUR")}
                  </div>
                </div>
                {group.rows.map((row) => {
                  const status = effectiveStatus(row);
                  const linkedExpense = row.linked_expense;
                  const duplicateRows = duplicateRowsById.get(row.client_row_id) || [];

                  return (
                    <div key={row.client_row_id} className="rounded-md border bg-background p-3">
                      <div className="grid gap-3 lg:grid-cols-[24px_120px_minmax(220px,1fr)_220px_120px_260px] lg:items-center">
                        <Checkbox
                          checked={row.selected}
                          disabled={status !== "ready"}
                          onCheckedChange={(checked) => updateRow(row.client_row_id, (current) => ({
                            ...current,
                            selected: Boolean(checked),
                          }))}
                        />
                        <div className="space-y-1">
                          <Badge variant={statusVariant(status)}>{statusLabel[status]}</Badge>
                          {duplicateRows.length > 0 && (
                            <Badge variant="outline" className="ml-1 border-amber-300 text-amber-800">
                              Possible duplicate
                            </Badge>
                          )}
                          <div className="text-xs text-muted-foreground">{row.transaction_date}</div>
                        </div>
                        <Input
                          value={row.draftDescription}
                          disabled={status === "imported" || status === "ignored"}
                          onChange={(event) => updateRow(row.client_row_id, (current) => ({
                            ...current,
                            draftDescription: event.target.value,
                          }))}
                        />
                        <Select
                          value={row.selectedCategoryId || "none"}
                          disabled={status === "imported" || status === "ignored"}
                          onValueChange={(value) => updateRow(row.client_row_id, (current) => ({
                            ...current,
                            selectedCategoryId: value === "none" ? "" : value,
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No category</SelectItem>
                            {categories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.emoji ? `${category.emoji} ` : ""}{category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="font-semibold">{formatCurrency(row.amount, row.currency_code)}</div>
                        <div className="flex flex-wrap gap-2">
                          {status === "imported" && (
                            <span className="text-sm text-muted-foreground">
                              {linkedExpense ? linkedExpense.description || linkedExpense.id : "Linked"}
                            </span>
                          )}
                          {status === "ignored" ? (
                            <Button size="sm" variant="outline" onClick={() => setIgnored([row], false)}>
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Undo
                            </Button>
                          ) : status === "imported" ? null : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => importRows([row])}
                                disabled={!canImport(row) || row.busy}
                              >
                                {status === "possible_match"
                                  ? "Import anyway"
                                  : status === "recurring_price_change"
                                    ? "Import as new"
                                    : "Import"}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setIgnored([row], true)} disabled={row.busy}>
                                Ignore
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {status === "recurring_price_change" && row.recurring_price_change && (
                        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                          <div className="font-medium">
                            Looks like "{row.recurring_price_change.rule_description}" with a price change
                          </div>
                          <div className="mt-1">
                            {formatCurrency(row.recurring_price_change.expected_amount, row.currency_code)}
                            {" → "}
                            {formatCurrency(row.recurring_price_change.actual_amount, row.currency_code)}
                            {" from "}
                            {row.recurring_price_change.occurrence_date}
                            {row.recurring_price_change.matched_by === "amount_band" && (
                              <span className="text-blue-700"> (matched by amount and date — double-check)</span>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => updateSubscriptionAndLink(row)} disabled={row.busy}>
                              Update subscription & link
                            </Button>
                          </div>
                          <p className="mt-2 text-xs text-blue-900/80">
                            Updates the subscription's amount from that date (rewriting its generated
                            transactions) and links this bank row. Use "Import as new" if this is really
                            a separate charge.
                          </p>
                        </div>
                      )}

                      {status === "possible_match" && row.possible_matches.length > 0 && (
                        <div className="mt-3 flex flex-col gap-2 rounded-md bg-muted/40 p-3 sm:flex-row sm:items-center">
                          <Select
                            value={row.selectedMatchId || row.possible_matches[0].id}
                            onValueChange={(value) => updateRow(row.client_row_id, (current) => ({
                              ...current,
                              selectedMatchId: value,
                            }))}
                          >
                            <SelectTrigger className="sm:max-w-md">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {row.possible_matches.map((match) => (
                                <SelectItem key={match.id} value={match.id}>
                                  {match.date} · {formatCurrency(match.amount, match.currency_code)} · {match.description || match.id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" onClick={() => linkExisting(row)} disabled={row.busy}>
                            <LinkIcon className="mr-2 h-4 w-4" />
                            Link existing
                          </Button>
                        </div>
                      )}

                      {duplicateRows.length > 0 && (
                        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                          <div className="font-medium">Similar rows in this upload</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {duplicateRows.map((duplicateRow) => (
                              <span key={duplicateRow.client_row_id} className="rounded-md border border-amber-200 bg-white/70 px-2 py-1 text-xs">
                                {duplicateRow.transaction_date} · {formatCurrency(duplicateRow.amount, duplicateRow.currency_code)} · {duplicateRow.normalized_label || duplicateRow.raw_label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
        )}
      </div>
    </ResponsiveContainer>
  );
};
